/**
 * IndexedDB API with support for multiple stores
 * Provides database management and access to various stores
 */

import Sentry from '../sentry';

// DB configuration
const DB_NAME = 'bomello-db';
const DB_VERSION = 1;

// Define store configurations
export interface StoreConfig {
  name: string;
  keyPath?: string | null;
  autoIncrement?: boolean;
  indexes?: {
    name: string;
    keyPath: string | string[];
    options?: IDBIndexParameters;
  }[];
}

// Default stores
const STORES: StoreConfig[] = [
  {
    name: 'keyvalue', // Main key-value store
    keyPath: null,
    autoIncrement: false
  }
];

// Create the IndexedDB API methods
export const createDbApi = () => {
  // Promise to ensure database is ready
  let dbPromise: Promise<IDBDatabase> | null = null;

  // Initialize the database with all configured stores
  const initDb = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        const error = (event.target as IDBOpenDBRequest).error;
        console.error('IndexedDB error:', error);
        Sentry.captureException(error);
        reject(error);
      };

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create all configured stores
        STORES.forEach(store => {
          if (!db.objectStoreNames.contains(store.name)) {
            const objectStore = db.createObjectStore(store.name, {
              keyPath: store.keyPath || undefined,
              autoIncrement: store.autoIncrement || false
            });

            // Create any indexes defined for the store
            if (store.indexes) {
              store.indexes.forEach(index => {
                objectStore.createIndex(index.name, index.keyPath, index.options);
              });
            }
          }
        });
      };
    });
  };

  // Get the database instance
  const getDb = (): Promise<IDBDatabase> => {
    if (!dbPromise) {
      dbPromise = initDb();
    }
    return dbPromise;
  };

  return {
    /**
     * Initialize the database
     */
    async init(): Promise<void> {
      try {
        await getDb();
      } catch (error) {
        console.error('Failed to initialize IndexedDB:', error);
        Sentry.captureException(error);
        throw error;
      }
    },

    /**
     * Reset the database connection
     * Useful for testing or handling connection issues
     */
    async resetConnection(): Promise<void> {
      dbPromise = null;
      await this.init();
    },

    /**
     * Key-value store operations
     */
    keyValueStore: {
      /**
       * Store a value with the given key
       * @param key The key to store the value under
       * @param value The value to store (can be any JSON-serializable object)
       */
      async set(key: string, value: any): Promise<void> {
        try {
          const db = await getDb();
          return new Promise((resolve, reject) => {
            const transaction = db.transaction('keyvalue', 'readwrite');
            const store = transaction.objectStore('keyvalue');

            const request = store.put(value, key);

            request.onsuccess = () => resolve();
            request.onerror = () => {
              Sentry.captureException(request.error, {
                tags: { operation: 'keyValueStore.set', key }
              });
              reject(request.error);
            };
          });
        } catch (error) {
          console.error(`Error setting value for key ${key}:`, error);
          Sentry.captureException(error, {
            tags: { operation: 'keyValueStore.set', key }
          });
          throw error;
        }
      },

      /**
       * Retrieve a value by key
       * @param key The key to retrieve
       * @returns The stored value, or null if not found
       */
      async get<T = any>(key: string): Promise<T | null> {
        try {
          const db = await getDb();
          return new Promise((resolve, reject) => {
            const transaction = db.transaction('keyvalue', 'readonly');
            const store = transaction.objectStore('keyvalue');

            const request = store.get(key);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => {
              Sentry.captureException(request.error, {
                tags: { operation: 'keyValueStore.get', key }
              });
              reject(request.error);
            };
          });
        } catch (error) {
          console.error(`Error getting value for key ${key}:`, error);
          Sentry.captureException(error, {
            tags: { operation: 'keyValueStore.get', key }
          });
          throw error;
        }
      },

      /**
       * Remove a value by key
       * @param key The key to remove
       */
      async remove(key: string): Promise<void> {
        try {
          const db = await getDb();
          return new Promise((resolve, reject) => {
            const transaction = db.transaction('keyvalue', 'readwrite');
            const store = transaction.objectStore('keyvalue');

            const request = store.delete(key);

            request.onsuccess = () => resolve();
            request.onerror = () => {
              Sentry.captureException(request.error, {
                tags: { operation: 'keyValueStore.remove', key }
              });
              reject(request.error);
            };
          });
        } catch (error) {
          console.error(`Error removing key ${key}:`, error);
          Sentry.captureException(error, {
            tags: { operation: 'keyValueStore.remove', key }
          });
          throw error;
        }
      },

      /**
       * Clear all stored key-value pairs
       */
      async clear(): Promise<void> {
        try {
          const db = await getDb();
          return new Promise((resolve, reject) => {
            const transaction = db.transaction('keyvalue', 'readwrite');
            const store = transaction.objectStore('keyvalue');

            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = () => {
              Sentry.captureException(request.error, {
                tags: { operation: 'keyValueStore.clear' }
              });
              reject(request.error);
            };
          });
        } catch (error) {
          console.error('Error clearing keyvalue store:', error);
          Sentry.captureException(error, {
            tags: { operation: 'keyValueStore.clear' }
          });
          throw error;
        }
      },

      /**
       * List all keys in the store
       * @returns Array of all keys
       */
      async keys(): Promise<string[]> {
        try {
          const db = await getDb();
          return new Promise((resolve, reject) => {
            const transaction = db.transaction('keyvalue', 'readonly');
            const store = transaction.objectStore('keyvalue');
            const keys: string[] = [];

            const request = store.openCursor();

            request.onsuccess = (event) => {
              const cursor = (event.target as IDBRequest).result;
              if (cursor) {
                keys.push(cursor.key as string);
                cursor.continue();
              } else {
                resolve(keys);
              }
            };

            request.onerror = () => {
              Sentry.captureException(request.error, {
                tags: { operation: 'keyValueStore.keys' }
              });
              reject(request.error);
            };
          });
        } catch (error) {
          console.error('Error getting keys from keyvalue store:', error);
          Sentry.captureException(error, {
            tags: { operation: 'keyValueStore.keys' }
          });
          throw error;
        }
      }
    },

    /**
     * Create a new store configuration for the next DB version
     * Note: This will only take effect after the database is upgraded
     *
     * @param storeConfig Configuration for the new store
     */
    addStoreConfig(storeConfig: StoreConfig): void {
      STORES.push(storeConfig);
      console.warn('Store configuration added. This will only take effect when DB_VERSION is incremented and app is reloaded.');
    },

    /**
     * Get a transaction for working directly with stores
     * For advanced usage when the standard API isn't sufficient
     */
    async getTransaction(storeNames: string | string[], mode: IDBTransactionMode = 'readonly'): Promise<IDBTransaction> {
      try {
        const db = await getDb();
        return db.transaction(storeNames, mode);
      } catch (error) {
        console.error(`Error getting transaction for store(s) ${storeNames}:`, error);
        Sentry.captureException(error, {
          tags: { operation: 'getTransaction', storeNames: Array.isArray(storeNames) ? storeNames.join(',') : storeNames }
        });
        throw error;
      }
    }
  };
};

// Define type for the DB API
export type DbApi = ReturnType<typeof createDbApi>;

// Default export with singleton instance for backward compatibility
// But we recommend using the context API instead
const indexedDbApi = createDbApi();
export default indexedDbApi;